// directions.js
// Utility to open navigation directions from user location to a spot
import { getUserLocation } from './location-utils.js';
import Popup from '../components/Popup.js';
import { getText, getCurrentLanguage } from '../translations/translations.js';
import { createIconElement } from './icon.js';

// Import domain settings for directions
import { DIRECTIONS_SETTINGS } from '/settings.js';

/**
 * Opens Google Maps directions from the user's current location to the given destination.
 * @param {Object} options
 * @param {number} options.lat - Destination latitude
 * @param {number} options.lng - Destination longitude
 * @param {string} [options.cid_googleMaps] - Google Maps CID (optional)
 */
export function openGoogleMapsDirections({ lat, lng, cid_googleMaps }) {
    if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
        console.log('[Directions][openGoogleMapsDirections] args:', { lat, lng, cid_googleMaps, USE_GOOGLE_CID: DIRECTIONS_SETTINGS?.USE_GOOGLE_CID });
    }
    // Only use cid_googleMaps if enabled in settings
    if (DIRECTIONS_SETTINGS?.USE_GOOGLE_CID && cid_googleMaps && typeof cid_googleMaps === 'string' && cid_googleMaps.length > 0) {
        const url = `https://www.google.com/maps?cid=${cid_googleMaps}`;
        window.open(url, '_blank', 'noopener');
        return;
    }
    // Always build a directions URL from user location to lat/lng
    const userLoc = getUserLocation();
    if (!userLoc || typeof lat !== 'number' || typeof lng !== 'number') {
        // Fallback: just open the destination in Google Maps
        const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
        window.open(url, '_blank', 'noopener');
        return;
    }
    const { latitude, longitude } = userLoc;
    const url = `https://www.google.com/maps/dir/?api=1&origin=${latitude},${longitude}&destination=${lat},${lng}`;
    window.open(url, '_blank', 'noopener');
}

/**
 * Opens Apple Maps directions from the user's current location to the given destination.
 * @param {Object} options
 * @param {number} options.lat - Destination latitude
 * @param {number} options.lng - Destination longitude
 */
export function openAppleMapsDirections({ lat, lng }) {
    const userLoc = getUserLocation();
    if (!userLoc || typeof lat !== 'number' || typeof lng !== 'number') {
        // Fallback: just open the destination in Apple Maps
        const url = `https://maps.apple.com/?q=${lat},${lng}`;
        window.open(url, '_blank', 'noopener');
        return;
    }
    const { latitude, longitude } = userLoc;
    // Apple Maps directions URL format
    const url = `https://maps.apple.com/?saddr=${latitude},${longitude}&daddr=${lat},${lng}&dirflg=d`;
    window.open(url, '_blank', 'noopener');
}

/**
 * Opens Waze directions from the user's current location to the given destination.
 * @param {Object} options
 * @param {number} options.lat - Destination latitude
 * @param {number} options.lng - Destination longitude
 */
export function openWazeDirections({ lat, lng }) {
    if (typeof lat !== 'number' || typeof lng !== 'number') {
        return;
    }
    // Waze navigation URL format
    const url = `https://www.waze.com/ul?ll=${lat},${lng}&navigate=yes`;
    window.open(url, '_blank', 'noopener');
}

/**
 * Shows a popup with navigation app options
 * @param {Object} options
 * @param {number} options.lat - Destination latitude
 * @param {number} options.lng - Destination longitude
 */
export async function showDirectionsPopup(args) {
    if (window.LOGGING && window.LOGGING.ENABLED) {
        console.log('[Directions][showDirectionsPopup] args:', args);
    }
    const { lat, lng, cid_googleMaps } = args;
    const lang = getCurrentLanguage();

    // Get translations
    const title = await getText('directions.title');
    const googleMapsText = await getText('directions.googleMaps');
    const appleMapsText = await getText('directions.appleMaps');
    const wazeText = await getText('directions.waze');


    // Create content with navigation app options (2 per row grid)
    const content = document.createElement('div');
    content.className = 'directions-popup-content';

    // Navigation apps array
    // Accept cid_googleMaps as an argument if available
    const apps = [
        {
            id: 'google',
            name: googleMapsText,
            icon: 'google-brands-solid-full',
            action: () => openGoogleMapsDirections({ lat, lng, cid_googleMaps })
        },
        {
            id: 'apple',
            name: appleMapsText,
            icon: 'apple-brands-solid-full',
            action: () => openAppleMapsDirections({ lat, lng })
        },
        {
            id: 'waze',
            name: wazeText,
            icon: 'waze-brands-solid-full',
            action: () => openWazeDirections({ lat, lng })
        }
    ];


    // Simple 2-column grid for buttons
    const gridDiv = document.createElement('div');
    gridDiv.className = 'directions-app-row-grid';

    // Add buttons in rows of 2
    for (let i = 0; i < apps.length; i += 2) {
        const rowDiv = document.createElement('div');
        rowDiv.className = 'directions-app-row';

        // First button in row
        const app1 = apps[i];
        const btn1 = document.createElement('button');
        btn1.className = 'directions-app-button';
        btn1.setAttribute('data-app', app1.id);
        btn1.setAttribute('aria-label', app1.name);
        btn1.appendChild(createIconElement(app1.icon, 'directions-app-icon'));
        const label1 = document.createElement('span');
        label1.className = 'directions-app-label';
        label1.textContent = app1.name;
        btn1.appendChild(label1);
        rowDiv.appendChild(btn1);

        // Second button in row (if exists)
        if (i + 1 < apps.length) {
            const app2 = apps[i + 1];
            const btn2 = document.createElement('button');
            btn2.className = 'directions-app-button';
            btn2.setAttribute('data-app', app2.id);
            btn2.setAttribute('aria-label', app2.name);
            btn2.appendChild(createIconElement(app2.icon, 'directions-app-icon'));
            const label2 = document.createElement('span');
            label2.className = 'directions-app-label';
            label2.textContent = app2.name;
            btn2.appendChild(label2);
            rowDiv.appendChild(btn2);
        } else {
            // If only one button in this row, center it
            rowDiv.classList.add('centered');
        }

        gridDiv.appendChild(rowDiv);
    }
    content.appendChild(gridDiv);

    // Create and show popup (no footer needed)
    const popupInstance = Popup({
        title: title,
        content: content,
        footer: '',
        size: 'extra-small',
        onClose: () => {
            // Cleanup if needed
        }
    });

    // Set up click handlers for app buttons
    let btnIndex = 0;
    for (let i = 0; i < gridDiv.children.length; i++) {
        const rowDiv = gridDiv.children[i];
        for (let j = 0; j < rowDiv.children.length; j++) {
            const appButton = rowDiv.children[j];
            const app = apps[btnIndex++];
            appButton.addEventListener('click', () => {
                app.action();
                popupInstance.close();
            });
        }
    }
}

/**
 * Returns a Google Maps link for a spot, using cid_googleMaps if available, else coordinates.
 * @param {number} lat
 * @param {number} lng
 * @param {string} [cid_googleMaps]
 */
export function getGoogleMapsLink(lat, lng, cid_googleMaps) {
    if (DIRECTIONS_SETTINGS?.USE_GOOGLE_CID && cid_googleMaps && typeof cid_googleMaps === 'string' && cid_googleMaps.length > 0) {
        return `https://www.google.com/maps?cid=${cid_googleMaps}`;
    }
    return `https://www.google.com/maps?q=${lat},${lng}`;
}

/**
 * Returns a Google Maps directions link for a spot, using cid_googleMaps if available, else coordinates.
 * @param {number} lat
 * @param {number} lng
 * @param {string} [cid_googleMaps]
 */
export function getGoogleMapsDirectionsLink(lat, lng, cid_googleMaps) {
    if (DIRECTIONS_SETTINGS?.USE_GOOGLE_CID && cid_googleMaps && typeof cid_googleMaps === 'string' && cid_googleMaps.length > 0) {
        return `https://www.google.com/maps?cid=${cid_googleMaps}`;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
}